import { MDBBtn } from 'mdb-react-ui-kit';
import React from 'react';
import { NavLink } from "react-router-dom";
import useLocale from '../util/i18n';
import { usePayload, useRbac } from '../util/token';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxesStacked, faChartLine, faCogs, faListCheck, faServer, faTimes, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';

export default function Nav() {
    const container = React.useRef<HTMLElement>(null);
    const {0: __} = useLocale();
    const {0: isGranted} = useRbac();
    const {0: payload} = usePayload();
    const doDismiss = React.useCallback((e: React.MouseEvent<HTMLElement> | boolean) => {
        if (e instanceof Event) {
            e.stopPropagation();
        }
        if (e !== true) {
            container.current?.classList.remove('active');
        }
        container.current?.querySelector('.nav.active')?.classList.remove('active');
    }, []);
    const onClick = React.useCallback((e: React.MouseEvent<HTMLElement> | { target: HTMLElement | null }) => {
        if (e instanceof Event) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (!(e.target as HTMLElement).classList.contains('nav-link')) {
            onClick({
                target: (e.target as HTMLElement).parentElement
            });
            return;
        }
        let n = (e.target as HTMLElement).nextElementSibling;
        if (!n) {
            return;
        }
        if (n.classList.contains('active')) {
            doDismiss(false);
        } else {
            doDismiss(true);
            container.current?.classList.add('active');
            n.classList.add('active');
        }
    }, [doDismiss]);
    return !payload || payload.admin == 3 ? null : (
        <nav className="nav-container" ref={container}>
            <div className="mask" onClick={doDismiss}></div>
            <ul className="nav flex-column">
                <li className="nav-item">
                    <NavLink to="/" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                        <FontAwesomeIcon icon={faChartLine} />
                        <span className="tip">{__('Dashboard')}</span>
                    </NavLink>
                </li>
                {isGranted('getInventories') ? (
                    <li className="nav-item">
                        <span className="nav-link" onClick={onClick}>
                            <FontAwesomeIcon icon={faListCheck} />
                            <span className="tip">{__('Inventory')}</span>
                        </span>
                        <dl className="nav flex-column">
                            <dt className="nav-title">
                                <span>{__('Inventory')}</span>
                                <MDBBtn color="link" onClick={doDismiss}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </MDBBtn>
                            </dt>
                            <dd className="nav-item">
                                <NavLink to="/inventory/shipment" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                    <span>{__('Shipment')}</span>
                                </NavLink>
                            </dd>
                            <dd className="nav-item">
                                <NavLink to="/inventory/purchase-order" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                    <span>{__('Purchase Order')}</span>
                                </NavLink>
                            </dd>
                        </dl>
                    </li>
                ) : null}
                {isGranted('getOrders') || isGranted('getShipments') || isGranted('getPurchaseOrders') || isGranted('getCreditMemos') || isGranted('getSuppliers') ? (
                    <li className="nav-item">
                        <span className="nav-link" onClick={onClick}>
                            <FontAwesomeIcon icon={faServer} />
                            <span className="tip">{__('Data')}</span>
                        </span>
                        <dl className="nav flex-column">
                            <dt className="nav-title">
                                <span>{__('Data')}</span>
                                <MDBBtn color="link" onClick={doDismiss}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </MDBBtn>
                            </dt>
                            {isGranted('getOrders') ? (
                                <dd className="nav-item">
                                    <NavLink to="/sales-order" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                        <span>{__('Order')}</span>
                                    </NavLink>
                                </dd>
                            ) : null}
                            {isGranted('getShipments') ? (
                                <dd className="nav-item">
                                    <NavLink to="/sales-shipment" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                        <span>{__('Shipment')}</span>
                                    </NavLink>
                                </dd>
                            ) : null}
                            {isGranted('getPurchaseOrders') ? (
                                <dd className="nav-item">
                                    <NavLink to="/purchase-order" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                        <span>{__('Purchase Order')}</span>
                                    </NavLink>
                                </dd>
                            ) : null}
                            {isGranted('getCreditMemos') ? (
                                <dd className="nav-item">
                                    <NavLink to="/credit-memo" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                        <span>{__('Credit Memo')}</span>
                                    </NavLink>
                                </dd>
                            ) : null}
                            {isGranted('getSuppliers') ? (
                                <dd className="nav-item">
                                    <NavLink to="/supplier" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                        <span>{__('Supplier')}</span>
                                    </NavLink>
                                </dd>
                            ) : null}
                        </dl>
                    </li>
                ) : null}

                {isGranted('getStock') ? (
                    <li className="nav-item">
                        <span className="nav-link" onClick={onClick}>
                            <FontAwesomeIcon icon={faBoxesStacked} />
                            <span className="tip">{__('Stock')}</span>
                        </span>
                        <dl className="nav flex-column">
                            <dt className="nav-title">
                                <span>{__('Stock Management')}</span>
                                <MDBBtn color="link" onClick={doDismiss}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </MDBBtn>
                            </dt>
                            <dd className="nav-item">
                                <NavLink to="/bin" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                    <span>{__('Bin Location')}</span>
                                </NavLink>
                            </dd>
                            <dd className="nav-item">
                                <NavLink to="/bin/history" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                    <span>{__('Bin History')}</span>
                                </NavLink>
                            </dd>
                            <dd className="nav-item">
                                <NavLink to="/stock/dispatch" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                    <span>{__('Stock Out History')}</span>
                                </NavLink>
                            </dd>
                            {isGranted(['getProductGroups', 'getProducts', 'getPurchaseOrderItem', 'getSoldQty', 'getStockQty', 'getRefundQty']) ? (
                                <dd className="nav-item">
                                    <NavLink to="/forecast" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                        <span>{__('Stock Forecast')}</span>
                                    </NavLink>
                                </dd>
                            ) : null}
                            <dd className="nav-item">
                                <NavLink to="/bin/inventory" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                    <span>{__('Stock Count')}</span>
                                </NavLink>
                            </dd>
                        </dl>
                    </li>
                ) : null}
                {isGranted('getAdminUser') ? (
                    <li className="nav-item">
                        <span className="nav-link" onClick={onClick}>
                            <FontAwesomeIcon icon={faUsers} />
                            <span className="tip">{__('User Management')}</span>
                        </span>
                        <dl className="nav flex-column">
                            <dt className="nav-title">
                                <span>{__('User Management')}</span>
                                <MDBBtn color="link" onClick={doDismiss}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </MDBBtn>
                            </dt>
                            <dd className="nav-item">
                                <NavLink to="/user/me" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                    <span>{__('My Profile')}</span>
                                </NavLink>
                            </dd>
                            <dd className="nav-item">
                                <NavLink to="/user" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                    <span>{__('User Management')}</span>
                                </NavLink>
                            </dd>
                            {isGranted('getAdminRole') ? (
                                <dd className="nav-item">
                                    <NavLink to="/role" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                        <span>{__('Role Management')}</span>
                                    </NavLink>
                                </dd>
                            ) : null}
                            {isGranted('getKPI') ? (
                                <>
                                    <dd className="nav-item">
                                        <NavLink to="/user/logs?filter[type]=1" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                            <span>{__('Assemble Logs')}</span>
                                        </NavLink>
                                    </dd>
                                    <dd className="nav-item">
                                        <NavLink to="/user/logs?filter[type]=2" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                            <span>{__('Organize Logs')}</span>
                                        </NavLink>
                                    </dd>
                                </>
                            ) : null}
                        </dl>
                    </li>
                ) : (
                    <li className="nav-item">
                        <NavLink to="/user/me" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                            <FontAwesomeIcon icon={faUser} />
                            <span className="tip">{__('My Profile')}</span>
                        </NavLink>
                    </li>
                )}
                <li className="nav-item">
                    <span className="nav-link" onClick={onClick}>
                        <FontAwesomeIcon icon={faCogs} />
                        <span className="tip">{__('Settings')}</span>
                    </span>
                    <dl className="nav flex-column">
                        <dt className="nav-title">
                            <span>{__('Settings')}</span>
                            <MDBBtn color="link" onClick={doDismiss}>
                                <FontAwesomeIcon icon={faTimes} />
                            </MDBBtn>
                        </dt>
                        <dd className="nav-item">
                            <NavLink to="/config/inventory" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                <span>{__('Inventory Settings')}</span>
                            </NavLink>
                        </dd>
                        <dd className="nav-item">
                            <NavLink to="/config/api" onClick={doDismiss} className={({ isActive }) => "nav-link" + (isActive ? ' active' : '')}>
                                <span>{__('API Settings')}</span>
                            </NavLink>
                        </dd>
                    </dl>
                </li>
            </ul>
        </nav>
    );
}
